import React, {useState} from 'react';
import './ToolbarHeader.sass';
import {
  Typography,
  Toolbar,
  FormControlLabel,
  Checkbox,
  ButtonProps,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import {useDispatch} from 'react-redux';
import {downloadFile} from 'Shared/utilities/fetchHelpers';
import {AsyncError, ServerError} from 'Core/models/Async';
import {createSnackbar} from 'Core/ducks/snackbar';
import PublishRoundedIcon from '@material-ui/icons/PublishRounded';
import 'Shared/components/FileDownloadLink.sass';
import {FileExtension} from 'Shared/components/FileDownloadLink';

type SelectAllProps = {
  disabled?: boolean;
  checked?: boolean;
  onChange?: ((event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void) | undefined;
};

export type ToolbarHeaderProps = {
  title?: string;
  children?: JSX.Element | JSX.Element[];
  selectAllProps?: SelectAllProps;
};

export type ToolbarHeaderIconProps = {
  title?: string;
  children?: JSX.Element | JSX.Element[];
  disabled?: boolean;
} & ButtonProps;

export type ExportToolbarHeaderIconProps = {
  downloadedFileName: string;
  extension?: FileExtension | undefined;
  relativeFileLocation: string;
  totalRecordCount: number;
} & ButtonProps;

export const ExportToolbarHeaderIcon = (props: ExportToolbarHeaderIconProps) => {
  const {downloadedFileName, extension = '', relativeFileLocation, title, disabled} = props;
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  return isLoading ? (
    <CircularProgress className="file-download-link__loading" />
  ) : (
    <ToolbarHeaderIcon
      title={title!}
      disabled={disabled}
      onClick={() => {
        downloadFile(relativeFileLocation, `${downloadedFileName}${extension}`, 'The export was successful.')
          .then((response: AsyncError | string) => {
            if (typeof response === 'string') {
              dispatch(createSnackbar(response));
            } else {
              if (response instanceof ServerError) dispatch(createSnackbar('An error occurred while exporting data.'));
              else dispatch(createSnackbar(response.message));
            }
          })
          .then(() => {
            setIsLoading(false);
          });

        setIsLoading(true);
      }}
    >
      <PublishRoundedIcon className="export-icon-rotate" />
    </ToolbarHeaderIcon>
  );
};

export const ExportToolbarHeaderIconWithCustomError = (props: ExportToolbarHeaderIconProps) => {
  const {downloadedFileName, extension = '', relativeFileLocation, title, disabled, totalRecordCount} = props;
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  return isLoading ? (
    <CircularProgress className="file-download-link__loading" />
  ) : (
    <ToolbarHeaderIcon
      title={title!}
      disabled={disabled}
      onClick={() => {
        if (totalRecordCount !== 0 && totalRecordCount > 5000) {
          dispatch(createSnackbar('Your results exceed 5,000 lines, please filter your data further.'));
          setIsLoading(false);
        } else {
          downloadFile(relativeFileLocation, `${downloadedFileName}${extension}`, 'The export was successful.')
            .then((response: AsyncError | string) => {
              if (typeof response === 'string') {
                dispatch(createSnackbar(response));
              } else {
                if (response instanceof ServerError)
                  dispatch(createSnackbar('An error occurred while exporting data.'));
                else dispatch(createSnackbar(response.message));
              }
            })
            .then(() => {
              setIsLoading(false);
            });
          setIsLoading(true);
        }
      }}
    >
      <PublishRoundedIcon className="export-icon-rotate" />
    </ToolbarHeaderIcon>
  );
};

export const ToolbarHeaderIcon = ({children, disabled, onClick, title}: ToolbarHeaderIconProps) => {
  return (
    <Tooltip title={title!}>
      <IconButton
        className="toolbar-header__content-container--button-action"
        onClick={onClick}
        aria-label={title}
        disabled={disabled}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
};

const ToolbarHeader = ({children, selectAllProps, title}: ToolbarHeaderProps) => {
  let selectAll;

  if (selectAllProps) {
    const {checked, disabled, onChange} = selectAllProps;

    selectAll = (
      <div className="toolbar-header__content-container--select-all">
        <FormControlLabel
          control={<Checkbox disabled={disabled!} checked={checked!} onChange={onChange!} />}
          label="Select All"
        />
      </div>
    );
  } else {
    selectAll = <></>;
  }
  return (
    <div className="toolbar-header">
      <Toolbar className="toolbar-header__content-container">
        <div className="select-all-checkbox-desktop">{selectAll}</div>
        <Typography className="toolbar-header__content-container--title" variant="h6">
          {title}
        </Typography>
        <div className="toolbar-header__content-container--tools">{children}</div>
      </Toolbar>
      <div className="select-all-checkbox-mobile">{selectAll}</div>
    </div>
  );
};

export default ToolbarHeader;
